/* eslint-disable jsx-a11y/interactive-supports-focus */
import React, { useState, useEffect } from "react";
import { connect, useSelector } from "react-redux";
import classNames from "classnames";
import { bool, func, arrayOf, string, shape, number, objectOf, oneOfType } from "prop-types";

import { selectors as sessionSelectors } from "reducers/session";
import { selectors as desktopSelectors, actions } from "reducers/desktop";
import { selectors as campaignsSelectors } from "reducers/campaigns";
import { selectors as loginSelectors } from "reducers/login";
import { selectors as onboardingSelectors, actions as onboardingActions } from "reducers/onboarding";
import { selectors as environmentSelectors, actions as environmentActions } from "reducers/environment";
import { CORPORATE_GROUP_ENVIRONMENT_TYPE, NEW_ENROLLMENT_ENVIRONMENT_TYPE } from "constants.js";
import * as stringUtils from "util/string";
import * as i18n from "util/i18n";
import gaEventTracker, { events } from "util/gaEventTracker";
import { push } from "connected-react-router";
import { Col, Row } from "react-bootstrap";

import Button from "pages/_components/Button";
import MainContainer from "pages/_components/MainContainer";
import Notification from "pages/_components/Notification";
import DraggableList from "pages/_components/DraggableList";
import Image from "pages/_components/Image";
import Head from "pages/_components/Head";
import * as Widgets from "pages/desktop/widgets";
import I18n from "pages/_components/I18n";
import Campaigns from "pages/campaigns/Campaigns";
import GeneralMsg from "pages/_components/GeneralMsg";
import Drawer from "pages/_components/Drawer/Drawer";
import CustomText from "pages/_components/CustomText/CustomText";
import ShortcutLink from "pages/_components/ShortcutLink";
import CustomData from "pages/_components/CustomData/CustomData";
import { useGetAccounts } from "hooks/useAccounts";
import BiometricIdentification from "./widgets/BiometricIdentification";
import EconomicGroups from "./widgets/economicGroup/EconomicGroups";
import { availableStepsAndPages, getNextStep } from "./utils";

const Desktop = (props) => {
    const {
        campaigns,
        sessionFetching,
        desktopFetching,
        displayCampaigns,
        isDesktop,
        dispatch,
        // availableWidgets,
        isEditable,
        activeEnvironment,
        layout,
        offers,
        firstName,
        currentStepRequest,
        isMobile,
        showAccountCreatedAlert,
    } = props;

    const {
        status: currentStepRequestStatus,
        value: currentStepRequestValue,
        error: currentStepRequestError,
    } = currentStepRequest;

    const [showFirstMsg, setShowFirstMsg] = useState(false);
    const [loading, setloading] = useState(false);
    const [stepStartMobile, setStepStartMobile] = useState(false);
    const [offerData, setOfferData] = useState(null);
    const [stepFinish, setstepFinish] = useState(null);

    const { role = "" } = useSelector((state) => state.onboarding.userRoleRequest.value);

    useGetAccounts();

    useEffect(() => {
        if (activeEnvironment?.type === NEW_ENROLLMENT_ENVIRONMENT_TYPE) {
            dispatch(onboardingActions.getUserRole());
        }
    }, [dispatch]);

    useEffect(() => {
        dispatch(actions.loadLayoutRequest());
        if (activeEnvironment?.type === NEW_ENROLLMENT_ENVIRONMENT_TYPE && isDesktop) {
            dispatch(actions.loadOffersRequest());
        }
    }, [activeEnvironment, isDesktop]);

    const startEnvironmentSteps = () => {
        setloading(true);
        dispatch(environmentActions.getCurrentStep());
    };

    useEffect(() => {
        if (activeEnvironment?.type === NEW_ENROLLMENT_ENVIRONMENT_TYPE) {
            if (!sessionFetching && (offers === "EMPTY" || offers === null)) {
                startEnvironmentSteps();
            } else if (!sessionFetching && offers && offers.length > 0) {
                if (offers?.[0]?.amount) {
                    gaEventTracker.triggerEvent(events.registry11);
                    setOfferData(offers[0]);
                }
            }
            if (!sessionFetching) {
                setShowFirstMsg(!isDesktop);
            }
        }
    }, [offers, sessionFetching]);

    useEffect(() => {
        if (activeEnvironment?.type === NEW_ENROLLMENT_ENVIRONMENT_TYPE) {
            if (stepStartMobile) {
                const { onboarding } = currentStepRequestValue;
                if (onboarding != null && onboarding?.status === "IN_REVIEW") {
                    dispatch(push(`/environment/StepFinish`));
                } else {
                    startEnvironmentSteps();
                }
            }
        }
    }, [stepStartMobile, activeEnvironment, currentStepRequestValue, dispatch]);

    const getActionName = (currentStep) => {
        switch (currentStep) {
            case "stepPaywayFirst":
                return null;
            case "stepSelectPaywayTerminalType":
                return "setPaywayTerminalTypeBackup";
            case "stepSelectPaywayCategory":
                return "setPaywayCategoryBackup";
            case "stepSavePaywayTerminalsData":
                return "setPaywayTerminalsDataBackup";
            case "lastStepPayway":
                return null;
            default:
                return `set${currentStep.slice(4)}Backup`;
        }
    };

    useEffect(() => {
        if (currentStepRequestStatus === "SUCCESS" && currentStepRequestValue) {
            const { currentStep, lastHistoric, onboarding } = currentStepRequestValue;
            if (onboarding != null && onboarding?.status === "IN_REVIEW") {
                setstepFinish(true);
                setloading(false);
                return;
            }
            if (!role) {
                return;
            }

            const nextStep = getNextStep(currentStep, role);
            if (currentStep && availableStepsAndPages.includes(currentStep)) {
                const actionName = getActionName(currentStep);
                if (actionName) {
                    dispatch(environmentActions?.[actionName]?.(JSON.parse(lastHistoric.extendedData)));
                }
                dispatch(push(`/environment/${nextStep}`));
            } else {
                dispatch(push("/environment/stepStart"));
            }
        } else if (currentStepRequestStatus === "FAILURE") {
            dispatch(push(`/environment/stepStart`));
        }
    }, [currentStepRequestStatus, currentStepRequestValue, currentStepRequestError, role, dispatch]);

    const getColumns = () =>
        layout.reduce((obj, item) => {
            const columnValue = obj[item.column] || [];
            return { ...obj, [item.column]: [...columnValue, item] };
        }, {});

    const handleClick = (item) => {
        const { column, row } = item;
        dispatch(actions.deleteWidget(layout.findIndex((w) => w.column === column && w.row === row)));
    };

    const handleItemsPositionChange = (items) => {
        dispatch(actions.setLayout(items));
        dispatch(actions.saveLayoutRequest());
    };

    const renderHeader = () => {
        if (!isDesktop) {
            return (
                <Head showCenterContent hasCenterContent>
                    <div className="col-5 py-6">
                        <h1 className="inverse" style={{ textAlign: "initial" }}>
                            Hola, <span className="highlighted">{props?.loggedUser?.userFullName}</span>
                        </h1>
                    </div>
                </Head>
            );
        }
        return null;
    };

    const renderItem = (item, { draggableItemProps }) => {
        const Widget = Widgets[stringUtils.capitalizeFirstLetter(item.id)];
        const buttonActionDesc = `${i18n.get("global.close")} ${i18n.get("global.widget")}, ${i18n.get(
            `list.addWidget.${item.id}`,
        )}`;
        return (
            <Widget
                className={classNames({ "draggable-list__item": isEditable })}
                closeButton={
                    isEditable && (
                        <Button className="btn-link btn-only-icon" onClick={() => handleClick(item)}>
                            <Image src="images/figma/icon/cross.svg" />
                            <span className="visually-hidden">{buttonActionDesc}</span>
                        </Button>
                    )
                }
                draggableItemProps={draggableItemProps}
                isEditable={isEditable}
                isDesktop={isDesktop}
                goTo={item.id}
            />
        );
    };

    if (stepFinish) {
        return (
            <>
                <Notification scopeToShow="desktop" />
                <Head />
                <MainContainer className="activeEnvironment" showLoader={sessionFetching || desktopFetching || loading}>
                    <div className="header-banner">
                        <div className="container">
                            <h1 className={isDesktop && "inverse"}>
                                <CustomText text={i18n.get("", firstName !== null ? "Hola" : "Hola,")} />
                                <CustomText text={firstName !== null ? ` ${firstName},` : ``} className="highlighted" />
                                <CustomText text={i18n.get("", "bienvenido a BiBank!")} />
                            </h1>
                        </div>
                    </div>
                    <div className="container">
                        <div>
                            <h2 className="title-content text-center">¡Terminamos!</h2>
                            <h2 className="title-content text-center">Completaste todos los pasos</h2>
                            <div className="illustration-alta-persona text-center">
                                <Image src="images/figma/illustrations/onboarding9.svg" className="svg-image" />
                            </div>
                            <div className="grid-table form-summary">
                                <p>
                                    {i18n.get(
                                        "",
                                        "Los datos y documentación van a ser analizados y en las próximas 48 hs. un Ejecutivo de Cuenta va a comunicarse con vos.",
                                    )}
                                </p>
                                <p>
                                    {i18n.get(
                                        "",
                                        "Asimismo, se enviará un email de invitación a las personas declaradas como firmantes.",
                                    )}
                                </p>
                            </div>
                            <div className="container--alert" role="alert">
                                <div className="alert alert-info">
                                    <CustomData
                                        icon="information_fill.svg"
                                        titleText="Recordá tener en tu poder los originales o copia certificada de la documentación."
                                        keepSpaceIfNoValue={false}
                                    />
                                </div>
                            </div>
                            <Row className="form-actions no-print">
                                <Col xs={12} md={4} mdOffset={4}>
                                    <Button
                                        bsStyle="primary"
                                        onClick={() => dispatch(push("/"))}
                                        label="global.finalize"
                                        loading={false}
                                        block
                                    />
                                </Col>
                            </Row>
                        </div>
                    </div>
                </MainContainer>
            </>
        );
    }

    if (activeEnvironment?.type === NEW_ENROLLMENT_ENVIRONMENT_TYPE && stepFinish === false) {
        return (
            <>
                <Notification scopeToShow="desktop" />
                <Head />
                <MainContainer
                    className="activeEnvironment"
                    showLoader={sessionFetching || desktopFetching || !offers || loading}>
                    <div className="header-banner">
                        <div className="container">
                            <h1 className={isDesktop && "inverse"}>
                                <CustomText text={i18n.get("", firstName !== null ? "Hola" : "Hola,")} />
                                <CustomText text={firstName !== null ? ` ${firstName},` : ``} className="highlighted" />
                                <CustomText text={i18n.get("", "bienvenido a BiBank!")} />
                            </h1>

                            <div className="banner-alta-persona box box-shadow banner md:grid-cols-2">
                                <div className="md:px-6 md:py-4">
                                    <h2>
                                        <CustomText
                                            text={i18n.get(
                                                "desktop.offers.text",
                                                "Ya tenes una cuenta corriente con acuerdo preaprobado por ",
                                            )}
                                        />
                                        <span className="whitespace-nowrap">
                                            <CustomText text={offerData?.amount} textType={offerData?.currency} />
                                            <CustomText text=" *" />
                                        </span>
                                    </h2>
                                    {isDesktop && (
                                        <p>
                                            <CustomText
                                                text={i18n.get(
                                                    "desktop.offers.warning",
                                                    "*CARTERA COMERCIAL. Sujeto a aprobación crediticia y condiciones de contratacion de BiBank.",
                                                )}
                                            />
                                        </p>
                                    )}
                                </div>
                                <div className="illustration text-center">
                                    <Image
                                        src="figma/illustrations/onboarding8desktop.svg"
                                        className="svg-image logo"
                                    />
                                </div>
                                {!isDesktop && (
                                    <p>
                                        <CustomText
                                            text={i18n.get(
                                                "",
                                                "*CARTERA COMERCIAL. Sujeto a aprobación crediticia y condiciones de contratacion de BiBank.",
                                            )}
                                        />
                                    </p>
                                )}
                            </div>
                        </div>
                    </div>

                    {isDesktop ? (
                        <div className="md:w-1/2" style={{ alignSelf: "center" }}>
                            <Button
                                defaultLabelText={i18n.get("", "Dar de alta tu empresa")}
                                onClick={() => startEnvironmentSteps()}
                                bsStyle="primary"
                                className="mt-7"
                            />
                        </div>
                    ) : (
                        <div style={{ paddingBottom: "30px" }}>
                            <Button
                                defaultLabelText={i18n.get("desktop.register.company.button", "Dar de alta tu empresa")}
                                onClick={() => startEnvironmentSteps()}
                                bsStyle="primary"
                                className="mt-7"
                            />
                        </div>
                    )}
                </MainContainer>

                <Drawer
                    className="qr-slider"
                    openSidebar={showFirstMsg}
                    toggleSidebar={() => setShowFirstMsg(false)}
                    {...props}>
                    <div className="container">
                        <h2 className="title-content text-center">
                            <CustomText text={i18n.get("", "Vamos a dar de alta a tu empresa")} />
                        </h2>
                        <div className="illustration-alta-persona text-center">
                            <Image src="images/figma/illustrations/onboarding7.svg" className="svg-image" />
                        </div>
                        <CustomText
                            text={i18n.get(
                                "",
                                "Te recomendamos hacer el proceso de alta desde tu computadora. Va a ser más fácil al momento de cargar la documentación obligatoria.",
                            )}
                        />
                        <Button
                            defaultLabelText={i18n.get("", "Quiero hacerlo desde aquí")}
                            onClick={() => {
                                setShowFirstMsg(false);
                                setStepStartMobile(true);
                            }}
                            bsStyle="primary"
                            className="mt-7"
                        />
                    </div>
                </Drawer>
            </>
        );
    }

    return (
        <>
            <Notification scopeToShow="desktop" autoDismiss={showAccountCreatedAlert ? "0" : "5"} />
            {renderHeader()}

            <MainContainer className="desktop" showLoader={sessionFetching || desktopFetching || loading}>
                {isDesktop && (
                    <div className="header-banner">
                        <div className="container">
                            <h1 className={isDesktop && "inverse"}>
                                Hola, <span className="highlighted">{props?.loggedUser?.userFullName}</span>
                            </h1>

                            {/* Banner desktop: leyenda + imagen pedidas */}
                            <div className="box box-shadow banner md:grid-cols-2">
                                <div className="banner-text">
                                    <h2>A la medida de tu empresa</h2>
                                </div>
                                <div className="illustration">
                                    <Image src="figma/illustrations/header_banner.svg" className="svg-image" />
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                <ShortcutLink isMobile={isMobile} />
                {displayCampaigns && campaigns && <Campaigns section="desktop-header" />}
                {activeEnvironment?.type === CORPORATE_GROUP_ENVIRONMENT_TYPE && <EconomicGroups />}

                {layout.length > 0 && (
                    <>
                        <DraggableList
                            columns={getColumns()}
                            isDragEnabled={isEditable}
                            itemRenderer={renderItem}
                            onItemsPositionChange={handleItemsPositionChange}
                        />
                        <BiometricIdentification />
                    </>
                )}

                {!isDesktop && (
                    <div className="box p-0 box-shadow banner md:grid-cols-2">
                        <div className="px-6 py-4">
                            <h2 className="custom-text">
                                <CustomText text={i18n.get("", "A la medida de tu empresa")} />
                            </h2>
                        </div>
                        <div className="illustration">
                            <Image src="figma/illustrations/header-banner-mobile.svg" className="svg-image" />
                        </div>
                    </div>
                )}

                {layout.length === 0 && !isEditable && activeEnvironment?.type !== CORPORATE_GROUP_ENVIRONMENT_TYPE && (
                    <GeneralMsg
                        imagePath="images/coloredIcons/desktop.svg"
                        title={<I18n id="widgets.list.empty.title" />}
                        description={
                            isDesktop ? (
                                <>
                                    <I18n id="widgets.list.empty.description" />
                                    <a href="#editDashboardBtn">
                                        <I18n id="desktop.editLayout.edit.label" />
                                    </a>
                                </>
                            ) : null
                        }
                    />
                )}
            </MainContainer>
        </>
    );
};

Desktop.propTypes = {
    dispatch: func.isRequired,
    layout: arrayOf(
        shape({
            column: number,
            id: string,
            row: number,
            uri: string,
        }),
    ).isRequired,
    offers: arrayOf(shape({})),
    isDesktop: bool,
    isMobile: bool,
    // availableWidgets: arrayOf(
    //     shape({
    //         column: number,
    //         id: string,
    //         row: number,
    //         uri: string,
    //     }),
    // ).isRequired,
    isEditable: bool,
    sessionFetching: bool,
    desktopFetching: bool,
    campaigns: arrayOf(
        shape({
            clickable: bool,
            contentType: string,
            creationDate: string,
            creator: string,
            dismissable: bool,
            endDate: string,
            endDateAsString: string,
            expired: bool,
            idCampaign: number,
            image: string,
            imageList: arrayOf(
                shape({
                    content: string,
                    creationDate: string,
                    fileName: string,
                    idCampaign: number,
                    idImage: number,
                    imageSize: objectOf(oneOfType([number, string])).isRequired,
                }),
            ).isRequired,
            name: string,
            priority: number,
            length: number,
            startDate: string,
            startDateAsString: string,
            suspended: bool,
            url: string,
        }),
    ).isRequired,
    activeEnvironment: shape({ type: string }).isRequired,
    displayCampaigns: bool,
    enabledAssistant: bool.isRequired,
    firstName: string.isRequired,
    path: string.isRequired,
    currentStepRequest: arrayOf(shape({})).isRequired,
    isLoggedIn: bool,
    loggedUser: string,
    showAccountCreatedAlert: bool.isRequired,
};

Desktop.defaultProps = {
    isDesktop: null,
    isMobile: null,
    isEditable: null,
    sessionFetching: null,
    desktopFetching: null,
    displayCampaigns: null,
    offers: null,
    isLoggedIn: false,
    loggedUser: null,
};

const mapStateToProps = (state) => ({
    // availableWidgets: desktopSelectors.getAvailableWidgets(state),
    layout: desktopSelectors.getLayout(state),
    offers: desktopSelectors.getOffers(state),
    isEditable: desktopSelectors.getIsEditale(state),
    campaigns: campaignsSelectors.getCampaigns(state),
    sessionFetching: sessionSelectors.isFetching(state),
    activeEnvironment: sessionSelectors.getActiveEnvironment(state),
    enabledAssistant: sessionSelectors.getEnabledAssistant(state),
    desktopFetching: desktopSelectors.isFetching(state),
    displayCampaigns: loginSelectors.getDisplayCampaigns(state),
    firstName: onboardingSelectors.getFirstName(state),
    currentStepRequest: environmentSelectors.getCurrentStepRequest(state),
    isLoggedIn: sessionSelectors.isLoggedIn(state),
    loggedUser: sessionSelectors.getUser(state),
    showAccountCreatedAlert: sessionSelectors.getShowAlertAccountCreated(state),
});

export default connect(mapStateToProps)(Desktop);
